import _ from 'underscore';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Stack } from '@fluentui/react/lib/Stack';
import { TextField } from '@fluentui/react/lib/TextField';
import { IconButton } from '@fluentui/react/lib/Button';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { tryParseTimeString, timeToString, preventEventPropagation } from '@bingads-webui-campaign-react/smart-component-util';

const getStyles = () => ({
  container: {
    margin: '10px 0px',
  },
  separator: {
    padding: '6px 10px',
  },
  timeInputField: {
    width: 185,
  },
  removeButton: {
    border: 'none',
    background: 'none',
    height: 32,
    fontSize: 16,
  },
});

const defaultTime = { hour: 0, minute: 0 };

export const UnStyledTimeRangeEditor = ({
  classes,
  i18n,
  isDisabled,
  isRemovable,
  index,
  open,
  close,
  onChange,
  onRemove,
  currentDay,
}) => {
  const timeFormat = 'hh:mm';

  const [openTime, setOpenTime] = useState(open);
  const [closeTime, setCloseTime] = useState(close);
  const [openTimeError, setOpenTimeError] = useState('');
  const [closeTimeError, setCloseTimeError] = useState('');

  const buildChangeEventData = (timeOpen, timeClose, isValid) => {
    const timeRange = {};

    timeRange.openHour = timeOpen ? timeOpen.hour : null;
    timeRange.openMinute = timeOpen ? timeOpen.minute : null;
    timeRange.closeHour = timeClose ? timeClose.hour : null;
    timeRange.closeMinute = timeClose ? timeClose.minute : null;

    return {
      index,
      isValid,
      ...timeRange,
    };
  };

  const openTimeChange = (e, val) => {
    if (_.isEmpty(val)) {
      setOpenTimeError(i18n.getString(_TL_('Required')));
      onChange(buildChangeEventData(null, closeTime, false));
      return;
    }

    const time = tryParseTimeString(val);

    if (time) {
      setOpenTime({ ...time });
      setOpenTimeError('');
      onChange(buildChangeEventData(time, closeTime, true));
      return;
    }

    setOpenTimeError(i18n.getString(_TL_('Invalid time')));
    onChange(buildChangeEventData(null, closeTime, false));
  };

  const closeTimeChange = (e, val) => {
    if (_.isEmpty(val)) {
      setCloseTimeError(i18n.getString(_TL_('Required')));
      onChange(buildChangeEventData(openTime, null, false));
      return;
    }

    const time = tryParseTimeString(val);

    if (time) {
      setCloseTime({ ...time });
      setCloseTimeError('');
      onChange(buildChangeEventData(openTime, time, true));
      return;
    }

    setCloseTimeError(i18n.getString(_TL_('Invalid time')));
    onChange(buildChangeEventData(openTime, null, false));
  };

  const onRemoveClick = (e) => {
    preventEventPropagation(e);
    onRemove(index);
  };

  return (
    <div
      className={classNames(classes.container, 'time-range-editor')}
      data-index={index}
    >
      <Stack horizontal horizontalAlign="space-between">
        <TextField
          className={classNames(classes.timeInputField, 'open-time')}
          defaultValue={timeToString(openTime, timeFormat)}
          onChange={openTimeChange}
          errorMessage={openTimeError}
          disabled={isDisabled}
          aria-required="true"
          ariaLabel={
            i18n.getString(_TL_('Opening time {{number}} for {{day}}'), {
              number: index + 1,
              day: currentDay,
            })
          }
        />
        <span className={classes.separator}>{i18n.getString(_TL_('-'))}</span>
        <TextField
          className={classNames(classes.timeInputField, 'close-time')}
          defaultValue={timeToString(closeTime, timeFormat)}
          onChange={closeTimeChange}
          errorMessage={closeTimeError}
          disabled={isDisabled}
          aria-required="true"
          ariaLabel={
            i18n.getString(_TL_('Closing time {{number}} for {{day}}'), {
              number: index + 1,
              day: currentDay,
            })
          }
        />
        {
          isRemovable && !isDisabled && (
            <span className="button-container">
              <IconButton
                className={classNames(classes.removeButton, 'remove-button')}
                onClick={onRemoveClick}
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel={
                  i18n.getString(_TL_('Remove open hours {{number}} for {{day}}'), {
                    number: index + 1,
                    day: currentDay,
                  })}
              />
            </span>
          )
        }
      </Stack>
    </div>
  );
};

UnStyledTimeRangeEditor.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    separator: PropTypes.string.isRequired,
    removeButton: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isRemovable: PropTypes.bool.isRequired,
  index: PropTypes.number,
  open: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
  }),
  close: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  currentDay: PropTypes.string.isRequired,
};

UnStyledTimeRangeEditor.defaultProps = {
  index: 0,
  open: defaultTime,
  close: defaultTime,
  onRemove: _.noop,
};

export const TimeRangeEditor = withDefaultStyles(
  UnStyledTimeRangeEditor,
  getStyles
);
